import { DateInput } from '../../../../components/DateInput/DateInput';
import React, { useState, useEffect } from 'react';
import { profileService, UserTaxInfoDto } from '../../../../services/profile.service';
import { toast } from 'react-toastify';
import { validateTaxInformation, localTodayIso } from '../../../../utils/investorValidation';
import { getTaxResidencyCountrySelectValue, normaliseCountryName } from '../../../../utils/formHelpers';
import { SharedFormContext } from '../shared/types';
import { useLiveErrorClearing } from '../shared/useLiveErrorClearing';
import { isResidentIndividual } from '../../../../config/profileVisibility';
import { useDelegationPermissions } from '../../../../contexts/DelegationPermissionsContext';
import { getPermissionErrorMessage } from '../../../../utils/apiClient';
import { PremiumSelect } from '../../../../components/PremiumSelect/PremiumSelect';

interface TaxInformationFormProps {
  initialData: UserTaxInfoDto | null;
  onSave: () => void;
  canEdit: boolean;
  canSubmit: boolean;
  isProxyMode: boolean;
  sharedContext: SharedFormContext;
}

export const TaxInformationForm: React.FC<TaxInformationFormProps> = ({
  initialData,
  onSave,
  canEdit,
  isProxyMode,
  sharedContext
}) => {
  const delegationPerms = useDelegationPermissions();
  const [formData, setFormData] = useState<Partial<UserTaxInfoDto>>({});

  // RI (user 2026-09-05): foreign-tax inputs (TIN, TIN type, tax residency certificate
  // no/date) do not apply to a Resident Indian — hidden here, skipped in validation.
  const isRi = isResidentIndividual(sharedContext.dashboardData?.investor?.investorType ?? undefined);

  // Lock rule for Name on PAN (user 2026-09-05): lock ONLY when the name came from an
  // ACCEPTED OCR document — every constituent part OCR-locked AND the joined field still
  // matches them. Intro-journey names have no OCR audit → stay editable; a hand-edited
  // name diverges from the joined parts → unlocks (same self-healing as H-15).
  const nameOnPanOcrLocked = ((): boolean => {
    const pi = sharedContext.personalInfo;
    if (!pi) return false;
    const joined = [pi.investorFirstName, pi.investorMiddleName, pi.investorLastName]
      .filter((p) => p && p.trim()).join(' ').trim();
    if (!joined || (formData.taxPanFirstName ?? '').trim() !== joined) return false;
    const firstOk = sharedContext.isOcrLocked('user_personal_information.investor_first_name', pi.investorFirstName);
    const lastOk = sharedContext.isOcrLocked('user_personal_information.investor_last_name', pi.investorLastName);
    const middleOk = !pi.investorMiddleName?.trim()
      || sharedContext.isOcrLocked('user_personal_information.investor_middle_name', pi.investorMiddleName);
    return firstOk && lastOk && middleOk;
  })();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // RI PAN details prefill (user 2026-09-05): PAN number and Name-on-PAN come from the
  // journey's personal info (OCR-filled on PAN accept) when the tax row doesn't already
  // hold values. Prefill-only — anything already saved on the tax row wins, and the
  // fields stay editable per the empty-stays-editable rule.
  useEffect(() => {
    if (!isRi) return;
    const pi = sharedContext.personalInfo;
    if (!pi) return;
    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      if (!(next.taxPanNo ?? '').trim() && (pi.panNumber ?? '').trim()) {
        next.taxPanNo = pi.panNumber;
        changed = true;
      }
      if (!(next.taxPanFirstName ?? '').trim()) {
        const joined = [pi.investorFirstName, pi.investorMiddleName, pi.investorLastName]
          .filter((p) => p && p.trim())
          .join(' ')
          .trim();
        if (joined) {
          next.taxPanFirstName = joined;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isRi, sharedContext.personalInfo]);

  // Laravel disables this select and posts the investor's country of residence
  // from a hidden input. Mirror that: derive the value when it isn't set yet,
  // otherwise a required field the investor can't edit would block the save.
  //
  // Prefer the backend's countryOfResidenceId — it is already a master_countries id, the same
  // table these options come from. The display name is only a fallback: it originates in
  // Dataverse / master_country_of_residence, which spell countries differently from
  // master_countries ("United States of America" vs "United States"), so an equality test on
  // the name silently finds nothing and leaves this disabled required field empty.
  useEffect(() => {
    const investor = sharedContext.dashboardData?.investor;
    if (!investor || sharedContext.taxResidencyCountries.length === 0) return;

    setFormData((prev) => {
      if (prev.taxResidencyCountryId || prev.taxResidencyCountry) return prev;

      const optionId = (c: (typeof sharedContext.taxResidencyCountries)[number]) => c.id ?? c.myRowId;

      let match = investor.countryOfResidenceId != null
        ? sharedContext.taxResidencyCountries.find((c) => optionId(c) === investor.countryOfResidenceId)
        : undefined;

      // Older backends (and any row the mapper could not place) send only the name.
      if (!match) {
        const residence = normaliseCountryName(investor.countryOfResidence);
        if (residence && residence !== 'na') {
          match = sharedContext.taxResidencyCountries.find(
            (c) => normaliseCountryName(c.ssName) === residence
          );
          if (!match) {
            // "United States of America" ⊃ "United States". Longest containment wins so a short
            // incidental substring cannot outrank the real country.
            for (const c of sharedContext.taxResidencyCountries) {
              const candidate = normaliseCountryName(c.ssName);
              if (!candidate) continue;
              if (residence.startsWith(candidate) || candidate.startsWith(residence)) {
                if (!match || normaliseCountryName(match.ssName).length < candidate.length) {
                  match = c;
                }
              }
            }
          }
        }
      }

      if (!match) return prev;
      const id = optionId(match);
      return { ...prev, taxResidencyCountry: String(id), taxResidencyCountryId: Number(id) };
    });
  }, [sharedContext.dashboardData, sharedContext.taxResidencyCountries]);

  // Corrected fields drop their error immediately (remove-only live re-check).
  useLiveErrorClearing(errors, setErrors, () => validateTaxInformation({ ...formData, tinNumber: formData.taxIdNumber, taxIdentificationNumberType: formData.taxIdentificationNumberType, fatcaStatus: formData.fatcaStatus, crsDeclaration: formData.crsDeclaration } as UserTaxInfoDto, { skipForeignTaxFields: isRi }), [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Map form fields to DTO for validation
    const validationData = {
      ...formData,
      tinNumber: formData.taxIdNumber,
      taxIdentificationNumberType: formData.taxIdentificationNumberType,
      fatcaStatus: formData.fatcaStatus,
      crsDeclaration: formData.crsDeclaration,
    };

    const newErrors = validateTaxInformation(validationData as UserTaxInfoDto,
      { skipForeignTaxFields: isRi });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError ? `Please fix: ${firstError}` : 'Please fix the errors in the form');
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const trimmedCountry =
        formData.taxResidencyCountry !== undefined && formData.taxResidencyCountry !== null
          ? String(formData.taxResidencyCountry).trim()
          : '';
      const payload: any = {
        ...formData,
        tinNumber: formData.taxIdNumber,
        taxResidencyCountry: trimmedCountry || undefined,
        taxIdentificationNumberType: formData.taxIdentificationNumberType,
        fatcaStatus: formData.fatcaStatus,
        crsDeclaration: formData.crsDeclaration,
      };
      await profileService.updateTaxInfo(payload);
      toast.success('Tax information updated');
      onSave();
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error(err.response?.data?.message || 'Failed to update');
      }
    } finally {
      setSaving(false);
    }
  };

  const taxResidencyCountrySelectValue = getTaxResidencyCountrySelectValue(formData);

  // Laravel keeps this read-only because it mirrors the country of residence captured at
  // registration. That is only safe while we actually have a value: a disabled field that is
  // also required and empty is a dead end — validation rejects the save and the investor has no
  // way to fix it. So fall back to letting them choose when nothing could be derived.
  const taxResidencyCountryLocked = taxResidencyCountrySelectValue !== '';

  return (
    <form className="investor-profile__card" onSubmit={handleSubmit}>
      {/* <h3>Tax Information</h3> */}
      {/* Laravel Section4 renders only #tax_info_oversees_div — the India/PAN
          block is toggled by a `tax_info` radio that the blade never renders,
          so it is permanently hidden. Same six fields for every investor type;
          Section4 has no per-type branching. */}
      <div className="investor-profile__grid">
        <div className="form-group">
          <label htmlFor="investor-tax-residency-country">
            Current Country of Residence for TAX <span className="text-danger">*</span>
          </label>
          <PremiumSelect
            id="investor-tax-residency-country"
            value={taxResidencyCountrySelectValue}
            onChange={(val) => {
              setFormData({
                ...formData,
                taxResidencyCountry: val || undefined,
                taxResidencyCountryId: val ? Number(val) : undefined,
              });
            }}
            options={sharedContext.taxResidencyCountries.map((c) => ({
              value: String(c.id ?? c.myRowId ?? ''),
              label: (c.ssName ?? c.ssCountry ?? `Country ${c.id}`).toUpperCase()
            }))}
            disabled={taxResidencyCountryLocked || !canEdit}
            searchable
          />
          {!taxResidencyCountryLocked && (
            <small className="text-muted">
              We could not derive this from your registered country of residence — please select it.
            </small>
          )}
          {errors.taxResidencyCountry && <div className="invalid-feedback">{errors.taxResidencyCountry}</div>}
        </div>

        {/* RI PAN details (user 2026-09-05): PAN number + Name on PAN saved into the
            Laravel tax_pan columns (taxPanNo / taxPanFirstName); DOB is display-only
            from Personal Info — the PAN's DOB IS the investor DOB, storing a copy
            would only let the two drift apart. */}
        {isRi && (
          <>
            <div className="form-group">
              <label>PAN Number <span className="text-danger">*</span></label>
              <input
                value={formData.taxPanNo ?? ''}
                onChange={(e) => setFormData({ ...formData, taxPanNo: e.target.value })}
                disabled={sharedContext.isOcrLocked('user_personal_information.user_pan_no', formData.taxPanNo)}
                title={sharedContext.isOcrLocked('user_personal_information.user_pan_no', formData.taxPanNo)
                  ? 'Verified from your document — change it in the Documents Center' : undefined}
                className={errors.taxPanNo ? 'form-control is-invalid' : 'form-control'}
              />
              {errors.taxPanNo && <div className="invalid-feedback">{errors.taxPanNo}</div>}
            </div>

            <div className="form-group">
              <label>Name on PAN <span className="text-danger">*</span></label>
              <input
                value={formData.taxPanFirstName ?? ''}
                onChange={(e) => setFormData({ ...formData, taxPanFirstName: e.target.value })}
                disabled={nameOnPanOcrLocked}
                title={nameOnPanOcrLocked
                  ? 'Verified from your document — change it in the Documents Center' : undefined}
                className={errors.taxPanFirstName ? 'form-control is-invalid' : 'form-control'}
              />
              {errors.taxPanFirstName && <div className="invalid-feedback">{errors.taxPanFirstName}</div>}
            </div>

            <div className="form-group">
              <label>Date of Birth (as per PAN)</label>
              <DateInput
                value={sharedContext.personalInfo?.userDob ?? ''}
                readOnly
                className="form-control"
                style={{ backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' }}
              />
            </div>
          </>
        )}

        {/* Foreign-tax block — NOT for RI (user 2026-09-05): a Resident Indian has no
            foreign TIN or tax residency certificate; validation skips these too. */}
        {!isRi && (
          <>
            <div className="form-group">
              <label>Taxpayer Identification Number in the country <span className="text-danger">*</span></label>
              <input
                value={formData.taxIdNumber ?? ''}
                onChange={(e) => setFormData({ ...formData, taxIdNumber: e.target.value })}
                className={errors.tinNumber ? 'form-control is-invalid' : ''}
              />
              {errors.tinNumber && <div className="invalid-feedback">{errors.tinNumber}</div>}
            </div>

            {/* Laravel renders this as a free-text input, not a picklist. */}
            <div className="form-group">
              <label>Taxpayer Identification Number type <span className="text-danger">*</span></label>
              <input
                value={formData.taxIdentificationNumberType ?? ''}
                onChange={(e) => setFormData({ ...formData, taxIdentificationNumberType: e.target.value })}
                className={errors.taxIdentificationNumberType ? 'form-control is-invalid' : ''}
              />
              {errors.taxIdentificationNumberType && <div className="invalid-feedback">{errors.taxIdentificationNumberType}</div>}
            </div>

            <div className="form-group">
              <label>Tax Residency Certificate Number (If Available)</label>
              <input
                value={formData.taxResidencyCertificateNo ?? ''}
                onChange={(e) => setFormData({ ...formData, taxResidencyCertificateNo: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Tax Residency Certificate Date</label>
              {/* Laravel parity: optional, but the calendar caps at today. */}
              <DateInput
                className={errors.taxResidencyCertificateDate ? 'form-control is-invalid' : 'form-control'}
                value={formData.taxResidencyCertificateDate ?? ''}
                onChange={(value) => setFormData({ ...formData, taxResidencyCertificateDate: value })}
                max={localTodayIso()}
              />
              {errors.taxResidencyCertificateDate && <div className="invalid-feedback d-block">{errors.taxResidencyCertificateDate}</div>}
            </div>
          </>
        )}

        {/* Laravel's single FATCA question: inline Yes/No radios bound to its
            `us_person_fatca` column, storing Laravel's exact "Yes"/"No" tokens.
            No default: an unanswered question shows neither radio and blocks Save
            (a FATCA declaration is never answered on the investor's behalf).
            Legacy rows may hold "YES"/"NO", so the checked test is case-insensitive. */}
        <div className="form-group">
          <label>Are you a US Person as defined under FATCA? <span className="text-danger">*</span></label>
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
              <input
                type="radio"
                name="usPersonFatca"
                value="Yes"
                checked={(formData.usPersonFatca ?? '').toUpperCase() === 'YES'}
                onChange={() => setFormData({ ...formData, usPersonFatca: 'Yes' })}
              /> Yes
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
              <input
                type="radio"
                name="usPersonFatca"
                value="No"
                checked={(formData.usPersonFatca ?? '').toUpperCase() === 'NO'}
                onChange={() => setFormData({ ...formData, usPersonFatca: 'No' })}
              /> No
            </label>
          </div>
          {errors.usPersonFatca && <div className="invalid-feedback d-block">{errors.usPersonFatca}</div>}
        </div>
      </div>

      {!sharedContext.lockedAfterSubmit && (
        <button type="submit" className="btn-save" disabled={saving || !canEdit}>
        {saving ? 'Submitting...' : 'Submit'}
        </button>
      )}
      {!canEdit && delegationPerms.isProxyMode && (
        <small className="text-warning d-block mt-2">
          You don't have permission to edit KYC information. Contact the investor to update delegation permissions.
        </small>
      )}
    </form>
  );
};
